import React, { useEffect } from "react";
import { Web3ReactProvider } from "@web3-react/core";
import { Flex, Heading, ThemeProvider, Paragraph, Link } from "theme-ui";
import ReactGA from "react-ga4";
import { BatchedWebSocketAugmentedWeb3Provider } from "@liquity/providers";
import { LiquityProvider } from "./hooks/LiquityContext";
import { WalletConnector } from "./components/WalletConnector";
import { TransactionProvider } from "./components/Transaction";
import { Icon } from "./components/Icon";
import { getConfig } from "./config";
import { ENV, validateEnv, isDebugEnabled } from "./config/env";
import theme from "./theme";

import { DisposableWalletProvider } from "./testUtils/DisposableWalletProvider";
import { LiquityFrontend } from "./LiquityFrontend";
import { AppLoader } from "./components/AppLoader";
import { ToastProvider } from "./components/ToastProvider";
import { CHAINS, SUPPORTED_CHAIN_IDS } from "./config/web3/chains";

// Validate environment configuration at startup
validateEnv();

if (window.ethereum) {
  // Silence MetaMask warning in console
  Object.assign(window.ethereum, { autoRefreshOnNetworkChange: false });
}

// Demo mode setup for testing without wallet
if (ENV.isDemoMode) {
  // Use env var private key, fallback to hardcoded test key (not recommended)
  const DEMO_PRIVATE_KEY =
    ENV.demoPrivateKey || "0x4d5db4107d237df6a3d58ee5f70ae63d73d7658d4026f2eefd2f204c81682cb7";

  if (!ENV.demoPrivateKey) {
    console.warn(
      "⚠️  Using hardcoded private key for demo mode. " +
        "Set REACT_APP_DEMO_PRIVATE_KEY in your .env file for security."
    );
  }

  const ethereum = new DisposableWalletProvider(
    ENV.rpcUrl || `http://${window.location.hostname}:8545`,
    DEMO_PRIVATE_KEY
  );

  Object.assign(window, { ethereum });
}

// Start pre-fetching the config
getConfig().then(config => {
  if (isDebugEnabled()) {
    console.log("Frontend config:", config);
    // Only expose to window in debug mode
    Object.assign(window, { config });
  }
});

const EthersWeb3ReactProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <Web3ReactProvider getLibrary={provider => new BatchedWebSocketAugmentedWeb3Provider(provider)}>
      {children}
    </Web3ReactProvider>
  );
};

const UnsupportedMainnetFallback: React.FC = () => (
  <Flex
    sx={{
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      height: "100vh",
      textAlign: "center"
    }}
  >
    <Heading sx={{ mb: 3 }}>
      <Icon name="exclamation-triangle" /> This app is for testing purposes only.
    </Heading>

    <Paragraph sx={{ mb: 3 }}>
      Please change your network to Ropsten, Rinkeby, Kovan, Görli or Kiln.
    </Paragraph>

    <Paragraph>
      If you'd like to use the Sable Protocol on mainnet, please pick a frontend{" "}
      <Link href="https://www.liquity.org/frontend">
        here <Icon name="external-link-alt" size="xs" />
      </Link>
      .
    </Paragraph>
  </Flex>
);

const App = () => {
  const unsupportedNetworkFallback = (chainId: number) => (
    <Flex
      sx={{
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        textAlign: "center"
      }}
    >
      <Heading sx={{ mb: 3 }}>
        <Icon name="exclamation-triangle" /> Sable is not yet deployed to this network
      </Heading>
      <span>
        Please switch to{" "}
        {SUPPORTED_CHAIN_IDS.map((chainId, index) => (
          <span key={chainId}>
            {CHAINS[chainId].name}
            {index !== SUPPORTED_CHAIN_IDS.length - 1 && " / "}
          </span>
        ))}
        .
      </span>
    </Flex>
  );

  useEffect(() => {
    if (ENV.googleTagId) {
      ReactGA.initialize(ENV.googleTagId, {
        testMode: !ENV.isProduction
      });
    }
  }, []);

  return (
    <EthersWeb3ReactProvider>
      <ThemeProvider theme={theme}>
        <ToastProvider>
          <WalletConnector loader={<AppLoader />}>
            <LiquityProvider
              loader={<AppLoader />}
              unsupportedNetworkFallback={unsupportedNetworkFallback}
              unsupportedMainnetFallback={<UnsupportedMainnetFallback />}
            >
              <TransactionProvider>
                <LiquityFrontend loader={<AppLoader />} />
              </TransactionProvider>
            </LiquityProvider>
          </WalletConnector>
        </ToastProvider>
      </ThemeProvider>
    </EthersWeb3ReactProvider>
  );
};

export default App;
